import React, { useEffect, useRef, useState } from 'react'
import { t } from 'ttag'
import { bgGray, veryLightBlue } from '../../styles/material-ui-theme-v2'
import Modal from '@material-ui/core/Modal'
import Backdrop from '@material-ui/core/Backdrop'
import Fade from '@material-ui/core/Fade'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { formatThousands } from '../../common/helpers/translations'
import { Haptics } from '@capacitor/haptics'
import { isNative } from '../../common/helpers/isNative'
import YumButton from '../YumButton/YumButton'
import { Box } from '@material-ui/core'
import GoalIcon from '../../assets/v3/icons/goal.png'
import { isRtl } from '../../i18nInit'

export interface IYumCustomEntryConfirmModal {
  children: any
  onSubmit: () => void
  date: string
  amount: number
  total: number
  className: string
  activityName: string
  units: string
  disabled: boolean
  digits: number
  binary?: boolean
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    position: 'relative',
    zIndex: 99,
    width: '345px',
    margin: '10px',
    borderRadius: '16px',
    outline: 'none',
    textAlign: 'center',
    padding: '24px',
    background: bgGray,
  },
  title: {
    fontWeight: 'bold',
    fontSize: 17,
    lineHeight: '20px',
  },
  content: {
    padding: 10,
    backgroundColor: 'white',
    borderRadius: 8,
    marginTop: 16,
    marginBottom: 16,
  },
  confirmButton: {
    display: 'inline-block',
    padding: '14px 16px',
    lineHeight: '18px',
    fontSize: 14,
    fontWeight: 'bold',
    borderRadius: 8,
    width: 'auto',
    minWidth: 'auto',
    margin: '0 5px',
    textTransform: 'none',
    border: '1px solid #006FFF',
  },
  cancelButton: {
    display: 'inline-block',
    padding: '14px 16px',
    lineHeight: '18px',
    fontSize: 14,
    fontWeight: 'bold',
    borderRadius: 8,
    width: 'auto',
    minWidth: 'auto',
    margin: '0 5px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    border: '1px solid #006FFF',
    color: '#006FFF',
    '&:hover, &:active, &:focus': {
      backgroundColor: veryLightBlue,
    },
  },
  date: {
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '16px',
    //color: '#24274B',
    opacity: '0.7',
  },
  amount: {
    fontWeight: 'bold',
    fontSize: '15px',
    lineHeight: '18px',
    marginTop: '2px',
    color: '#24274B',
    '& span': {
      fontWeight: 'bold',
      color: '#24A15C',
    },
    '& strong': {
      fontWeight: 'bold',
      color: '#D7263D',
    },
  },
  info: {
    fontSize: '14px',
  },
}))

function usePrevious(value: any) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export default function YumCustomEntryConfirmModal(props: IYumCustomEntryConfirmModal) {
  const { children, onSubmit, date, amount, className, activityName, units, total, disabled, binary, digits } = props
  const classes = useStyles()
  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    // @ts-ignore
    if (amount === 0 || amount === '-' || amount === '' || String(amount).endsWith('.')) return
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const onConfirm = () => {
    onSubmit()
    handleClose()
    if (isNative()) Haptics.notification()
  }

  const addAmount = formatThousands(amount)
  const removeAmount = formatThousands(Math.abs(amount))

  const totalFormatted = formatThousands(total, digits)

  return (
    <>
      <Modal
        className={classes.modal}
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <Box className={classes.modalContent}>
            <Box className={classes.title}>{activityName}</Box>
            {!binary && (
              <Box className={classes.info}>
                {amount >= 0 ? (
                  <>
                    {t`Adding`} {addAmount} {units}
                  </>
                ) : (
                  <>
                    {t`Removing`} {removeAmount} {units}
                  </>
                )}
              </Box>
            )}

            <Box className={classes.content}>
              <Box style={{ display: 'flex', textAlign: 'left' }}>
                <img
                  src={GoalIcon}
                  width="32"
                  height="40"
                  alt="Goal icon"
                  style={{
                    width: '32px',
                    flexShrink: 0,
                    marginRight: isRtl ? 0 : '10px',
                    marginLeft: isRtl ? '10px' : 0,
                  }}
                />
                <Box style={{ flexGrow: 1 }}>
                  <Box className={classes.date}>{date}</Box>
                  <Box className={classes.amount}>
                    {binary ? (
                      <>{total === 1 ? <span>{t`Marked as done`}</span> : <strong>{t`Marked as undone`}</strong>}</>
                    ) : (
                      <>
                        {t`Total`} <span>{totalFormatted}</span> {units}
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <YumButton onClick={handleClose} className={classes.cancelButton}>
              {t`Cancel`}
            </YumButton>
            <YumButton onClick={onConfirm} className={classes.confirmButton}>
              {t`Confirm`}
            </YumButton>
          </Box>
        </Fade>
      </Modal>
      <YumButton onClick={handleOpen} className={className} disabled={disabled}>
        {children}
      </YumButton>
    </>
  )
}
